import type { Group, Scene } from "three";
import type { Octree } from "three/examples/jsm/math/Octree.js";
import type { MissionDefinition } from "../scripting/api";

export type GameModeId = "survival" | "dm" | "tdm" | "ctf";
export type TeamId = "alpha" | "bravo";
export type Vec3Tuple = [number, number, number];

export interface SpawnPoint {
  id: string;
  position: Vec3Tuple;
  yaw?: number;
  team?: TeamId;
  tags?: string[];
}

export interface TriggerVolume {
  id: string;
  kind: "box";
  center: Vec3Tuple;
  size: Vec3Tuple;
  event: string;
  once?: boolean;
}

export interface NavNode {
  id: string;
  position: Vec3Tuple;
}

export interface NavGraph {
  nodes: NavNode[];
  links: Array<[string, string]>;
}

export interface LevelMarker {
  id: string;
  type: "flag-base" | "pickup" | "objective" | "spawn";
  position: Vec3Tuple;
  team?: TeamId;
}

export interface LightingHints {
  fogColor?: string;
  fogNear?: number;
  fogFar?: number;
  backgroundColor?: string;
}

export interface ProceduralBuildResult {
  levelRoot: Group;
  enemySpawns?: Vec3Tuple[];
}

export interface ProceduralBuildContext {
  scene: Scene;
  octree: Octree;
}

export interface LevelDefinition {
  id: string;
  name: string;
  description: string;
  thumbnail: string;
  gltfPath?: string;
  supportedModes: GameModeId[];
  spawnPoints: {
    singleplayer: SpawnPoint[];
    multiplayer: SpawnPoint[];
  };
  triggerVolumes: TriggerVolume[];
  navGraph: NavGraph;
  markers: LevelMarker[];
  lighting?: LightingHints;
  missions: MissionDefinition[];
  proceduralFactory?: (context: ProceduralBuildContext) => ProceduralBuildResult | Promise<ProceduralBuildResult>;
}

export interface GameModeDefinition {
  id: GameModeId;
  name: string;
  shortLabel: string;
  description: string;
  teamBased: boolean;
  teams?: TeamId[];
  fragLimit?: number;
  timeLimitSeconds?: number;
}

export interface CortexOptions {
  mouseSensitivity: number;
  invertY: boolean;
  fov: number;
  masterVolume: number;
  sfxVolume: number;
  musicVolume: number;
  frameLimiter: number;
}

export const defaultCortexOptions: CortexOptions = {
  mouseSensitivity: 1,
  invertY: false,
  fov: 74,
  masterVolume: 0.85,
  sfxVolume: 0.8,
  musicVolume: 0.55,
  frameLimiter: 60,
};

export function getSpawnForMode(level: LevelDefinition, mode: GameModeDefinition, team?: TeamId) {
  const pool = mode.id === "survival" ? level.spawnPoints.singleplayer : level.spawnPoints.multiplayer;
  if (team) {
    const teamSpawn = pool.find((spawn) => spawn.team === team);
    if (teamSpawn) {
      return teamSpawn;
    }
  }
  return pool[0] ?? level.spawnPoints.singleplayer[0];
}

export function validateLevelDefinition(level: LevelDefinition) {
  const issues: string[] = [];

  if (!level.spawnPoints.singleplayer.length) {
    issues.push(`${level.id}: missing singleplayer spawn points.`);
  }

  if (!level.spawnPoints.multiplayer.length) {
    issues.push(`${level.id}: missing multiplayer spawn points.`);
  }

  if (!level.thumbnail) {
    issues.push(`${level.id}: missing thumbnail.`);
  }

  if (!level.missions.length) {
    issues.push(`${level.id}: missing missions.`);
  }

  return issues;
}